/**
 * Gallery Admin page — manage projects for gallery display.
 * Create share links, copy embed codes, view analytics.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { Button, Card, CardContent, cn } from '@frameit/ui';
import * as api from '../lib/api-client';
import { copyToClipboard } from '../lib/clipboard';
import { logger } from '../lib/logger';
import type { ProjectListItem, GalleryShare } from '@frameit/types';

const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const isDevMode = !hasClerk;

/** Manage shares for a single project */
function ProjectShareCard({ project }: { project: ProjectListItem }): React.JSX.Element {
  const [shares, setShares] = useState<GalleryShare[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadShares = useCallback(async (): Promise<void> => {
    if (isDevMode) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.listShares(project.id);
      setShares(result);
    } catch (err) {
      logger.error('Failed to load shares:', err);
      setError('Failed to load shares');
    } finally {
      setIsLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    if (isOpen) loadShares();
  }, [isOpen, loadShares]);

  const handleCreateShare = async (): Promise<void> => {
    if (isDevMode) return;
    setError(null);
    try {
      const share = await api.createShare(project.id, { label: 'Gallery embed' });
      setShares((prev) => [share, ...prev]);
    } catch (err) {
      logger.error('Failed to create share:', err);
      setError('Failed to create share link');
    }
  };

  const handleRevokeShare = async (shareId: string): Promise<void> => {
    if (isDevMode) return;
    setError(null);
    try {
      await api.revokeShare(project.id, shareId);
      setShares((prev) => prev.map((s) => (s.id === shareId ? { ...s, isActive: false } : s)));
    } catch (err) {
      logger.error('Failed to revoke share:', err);
      setError('Failed to revoke share link');
    }
  };

  const handleCopy = async (text: string, id: string): Promise<void> => {
    await copyToClipboard(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-medium">{project.name}</h3>
            <p className="text-xs text-muted-foreground">
              {project.artworkCount} artwork{project.artworkCount !== 1 ? 's' : ''} &middot; Updated{' '}
              {new Date(project.updatedAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/editor/${project.id}`}>
              <Button variant="ghost" size="sm">
                Edit
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? 'Hide' : 'Shares'}
            </Button>
          </div>
        </div>

        {/* Shares section */}
        {isOpen && (
          <div className="mt-3 pt-3 border-t space-y-3">
            {/* Error message */}
            {error && (
              <div className="flex items-center justify-between px-3 py-2 rounded-md bg-destructive/10 text-destructive text-xs">
                <span>{error}</span>
                <button
                  onClick={() => {
                    setError(null);
                    loadShares();
                  }}
                  className="ml-2 underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Share Links</h4>
              <Button size="sm" onClick={handleCreateShare} disabled={isDevMode}>
                <svg
                  className="w-3.5 h-3.5 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                New Link
              </Button>
            </div>

            {isLoading && <p className="text-xs text-muted-foreground animate-pulse">Loading...</p>}

            {isDevMode && (
              <p className="text-xs text-muted-foreground">Share links require authentication</p>
            )}

            {shares.length === 0 && !isLoading && !isDevMode && (
              <p className="text-xs text-muted-foreground">No share links yet</p>
            )}

            {shares.map((share) => {
              const embedUrl = api.getEmbedUrl(share.token);
              const iframeCode = `<iframe src="${embedUrl}" width="600" height="500" frameborder="0" style="border:none;border-radius:8px;"></iframe>`;

              return (
                <div
                  key={share.id}
                  className={cn(
                    'p-3 rounded-lg border text-sm',
                    share.isActive ? 'bg-background' : 'bg-muted/50 opacity-60',
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'w-2 h-2 rounded-full',
                          share.isActive ? 'bg-green-500' : 'bg-muted-foreground',
                        )}
                      />
                      <span className="font-medium text-xs">
                        {share.label || share.token.substring(0, 16) + '...'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      {share.viewCount}
                    </div>
                  </div>

                  {share.isActive && (
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => handleCopy(embedUrl, `url-${share.id}`)}
                        className="px-2 py-1 text-xs rounded border hover:bg-accent transition-colors"
                      >
                        {copied === `url-${share.id}` ? 'Copied!' : 'Copy URL'}
                      </button>
                      <button
                        onClick={() => handleCopy(iframeCode, `iframe-${share.id}`)}
                        className="px-2 py-1 text-xs rounded border hover:bg-accent transition-colors"
                      >
                        {copied === `iframe-${share.id}` ? 'Copied!' : 'Copy Embed'}
                      </button>
                      <button
                        onClick={() => handleRevokeShare(share.id)}
                        className="px-2 py-1 text-xs rounded border text-destructive hover:bg-destructive/10 transition-colors ml-auto"
                      >
                        Revoke
                      </button>
                    </div>
                  )}

                  {!share.isActive && (
                    <span className="text-xs text-muted-foreground">Revoked</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DEV_PROJECTS: ProjectListItem[] = [
  { id: 'prj_dev', name: 'Dev Project', artworkCount: 1, updatedAt: new Date().toISOString() },
];

/** Gallery admin page */
export function GalleryPage(): React.JSX.Element {
  const [projects, setProjects] = useState<ProjectListItem[]>(isDevMode ? DEV_PROJECTS : []);
  const [isLoading, setIsLoading] = useState(!isDevMode);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProjects = useCallback((): void => {
    if (isDevMode) return;
    setIsLoading(true);
    setLoadError(null);

    api
      .listProjects(1, 50)
      .then((result) => {
        setProjects(result.items);
        setIsLoading(false);
      })
      .catch((err) => {
        logger.error('Failed to load projects:', err);
        setLoadError('Failed to load projects');
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="h-14 border-b flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <h1 className="font-semibold">FrameIt</h1>
          <span className="text-sm text-muted-foreground">Gallery</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/projects">
            <Button variant="ghost" size="sm">
              Projects
            </Button>
          </Link>
          <Link to="/editor">
            <Button variant="ghost" size="sm">
              Editor
            </Button>
          </Link>
          {hasClerk ? (
            <UserButton afterSignOutUrl="/auth/sign-in" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
              D
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold mb-1">Gallery Management</h2>
          <p className="text-sm text-muted-foreground">
            Create share links and embed codes for your framed artworks.
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-5 bg-muted rounded w-1/3 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Load error */}
        {loadError && !isLoading && (
          <div className="text-center py-12">
            <p className="text-sm text-destructive mb-3">{loadError}</p>
            <Button variant="outline" size="sm" onClick={loadProjects}>
              Retry
            </Button>
          </div>
        )}

        {/* Projects */}
        {!isLoading && !loadError && (
          <div className="space-y-4">
            {projects.map((project) => (
              <ProjectShareCard key={project.id} project={project} />
            ))}

            {projects.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No projects yet</p>
                <Link to="/editor">
                  <Button>Create a Project</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
